import { useState, useRef, useCallback, useEffect } from "react";
import { Progress } from "./ui/progress";
import { Slider } from "./ui/slider";
import { Pause, Play, Volume2, VolumeX } from "lucide-react";
import { getSoundPrefs, setSoundPrefs, playSound, speakBreathCue, SOUND_TYPES, primeBreathCues, primeSilentAudioSession } from "../lib/soundEngine";

const BinauralPlayerStandalone = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentRep, setCurrentRep] = useState(0);
  const [currentSide, setCurrentSide] = useState("left");
  const [volume, setVolume] = useState(() => getSoundPrefs().volume);
  const [voiceVolume] = useState(() => getSoundPrefs().voiceVolume);
  const [isMuted, setIsMuted] = useState(false);
  const [soundType] = useState(() => getSoundPrefs().soundType);

  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const startTimeoutRef = useRef(null); // tracks the 3s lead-in timeout
  const volumeRef = useRef(volume);
  const voiceVolumeRef = useRef(voiceVolume);
  const mutedRef = useRef(false);
  const soundTypeRef = useRef(soundType);

  const TOTAL_RIGHT_BEATS = 50;
  const BPM = 114;
  const BEAT_INTERVAL = 60000 / BPM;

  useEffect(() => { volumeRef.current = volume; }, [volume]);
  useEffect(() => { voiceVolumeRef.current = voiceVolume; }, [voiceVolume]);
  useEffect(() => { mutedRef.current = isMuted; }, [isMuted]);

  const getOrCreateContext = useCallback(() => {
    if (!audioContextRef.current || audioContextRef.current.state === "closed") {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      // Route WKWebView Web Audio through the app's playback session so
      // bilateral tones ignore the phone's mute switch (matching HTML
      // audio narration behaviour).
      primeSilentAudioSession();
    }
    if (audioContextRef.current.state === "suspended") {
      audioContextRef.current.resume();
    }
    return audioContextRef.current;
  }, []);

  const startPlaying = useCallback(() => {
    primeBreathCues(); // warm cue cache + unlock iOS audio inside user gesture
    const ctx = getOrCreateContext();
    setIsPlaying(true);
    setCurrentRep(0);
    setCurrentSide("left");

    let beatIndex = 0;
    let rightCount = 0;

    // Brief 3-second lead-in before the first beat so the user has a moment
    // to settle. Cancellable via stopPlaying.
    startTimeoutRef.current = setTimeout(() => {
      startTimeoutRef.current = null;
      const v = mutedRef.current ? 0 : volumeRef.current;
      playSound(ctx, soundTypeRef.current, "left", v);

      intervalRef.current = setInterval(() => {
        beatIndex++;
        const side = beatIndex % 2 === 0 ? "left" : "right";
        const vol = mutedRef.current ? 0 : volumeRef.current;
        if (side === "right") {
          rightCount++;
          setCurrentRep(rightCount);
        }
        setCurrentSide(side);
        playSound(ctx, soundTypeRef.current, side, vol);
        if (rightCount >= TOTAL_RIGHT_BEATS) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
          setIsPlaying(false);
          // Gentle spoken cue at the end of the bilateral sound — real recorded
          // voice, picked randomly without repeats. Volume scales with the slider.
          // Delay leaves a clear breath of silence between the last bilateral
          // beat and the spoken cue.
          setTimeout(() => {
            const vv = mutedRef.current ? 0 : voiceVolumeRef.current;
            speakBreathCue(vv);
          }, 1800);
        }
      }, BEAT_INTERVAL);
    }, 3000);
  }, [getOrCreateContext, BEAT_INTERVAL, TOTAL_RIGHT_BEATS]);

  const stopPlaying = useCallback(() => {
    setIsPlaying(false);
    if (startTimeoutRef.current) {
      clearTimeout(startTimeoutRef.current);
      startTimeoutRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopPlaying();
      if (audioContextRef.current && audioContextRef.current.state !== "closed") {
        audioContextRef.current.close();
      }
    };
  }, [stopPlaying]);

  const progressPercent = (currentRep / TOTAL_RIGHT_BEATS) * 100;
  const soundLabel = SOUND_TYPES.find((s) => s.id === soundType)?.name || "Wooden Click";

  return (
    <div className="binaural-player p-6 rounded-2xl text-white" data-testid="standalone-binaural-player">
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-sm text-white/70">Bilateral Sound &middot; {soundLabel}</p>
          <p className="text-lg font-medium">114 BPM &middot; {TOTAL_RIGHT_BEATS} cycles</p>
        </div>
        <button
          onClick={() => setIsMuted(!isMuted)}
          className="p-2 rounded-full hover:bg-white/10 transition-colors"
          data-testid="standalone-mute-btn"
        >
          {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
      </div>

      <div className="flex items-center gap-4 mb-4">
        <button
          onClick={isPlaying ? stopPlaying : startPlaying}
          className="w-14 h-14 bg-[#3090A8] hover:bg-[#7A9677] rounded-full flex items-center justify-center transition-colors"
          data-testid="standalone-play-pause-btn"
        >
          {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6 ml-1" />}
        </button>
        <div className="flex-1">
          <div className="flex items-center justify-between text-sm mb-2">
            <span>Cycle {currentRep} of {TOTAL_RIGHT_BEATS}</span>
            <span>{Math.round(progressPercent)}%</span>
          </div>
          <Progress value={progressPercent} className="h-2 bg-white/20" />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Volume2 className="w-4 h-4 text-white/70" />
        <Slider value={[volume * 100]} onValueChange={(val) => {
          const v = val[0] / 100;
          setVolume(v);
          setSoundPrefs({ ...getSoundPrefs(), volume: v });
        }} max={100} step={1} className="flex-1" data-testid="standalone-volume-slider" />
      </div>

      {isPlaying && (
        <p className="text-center text-sm text-white/70 mt-4 animate-pulse-slow">
          Let your mind float back to an earlier time you felt this way...
        </p>
      )}
    </div>
  );
};

export default BinauralPlayerStandalone;
